/* ==============================================================================
   Protocolo do gateway WebSocket
   ==============================================================================
   Contrato único entre servidor e cliente. Os schemas zod aqui são a fonte da
   verdade: o servidor valida a entrada com eles, o cliente deriva os tipos
   deles, e não existe uma segunda definição para sair de sincronia.

   Endpoint: wss://<host>/gateway?v=1

   Duas responsabilidades no mesmo socket:
     1. eventos de domínio (mensagem, presença, digitando)
     2. sinalização WebRTC (offer/answer/ICE) — relay cego entre dois pares

   Ver docs/ARCHITECTURE.md §7.
   ============================================================================== */

import { z } from 'zod';

/** Versão do protocolo, no query string. Bump só em mudança que quebra. */
export const GATEWAY_VERSION = 1;

/* ------------------------------------------------------------------------------
   Opcodes
   ------------------------------------------------------------------------------
   Numéricos e com lacunas deliberadas entre os grupos: acrescentar um opcode de
   voz não deve empurrar os de domínio, porque o número vai no fio e clientes
   antigos continuam falando.
------------------------------------------------------------------------------ */
export const Op = {
  /** S→C · evento de domínio; carrega `s` (sequência) para o RESUME */
  DISPATCH: 0,
  /** C→S · keepalive com o último `s` recebido */
  HEARTBEAT: 1,
  /** C→S · autentica com o access token */
  IDENTIFY: 2,
  /** C→S · status e atividade */
  PRESENCE_UPDATE: 3,
  /**
   * C→S · "estou digitando neste canal"
   *
   * Opcode 4 e não 5: o intervalo 0–11 é o de controle de sessão, e 4 é o
   * primeiro livre depois de `PRESENCE_UPDATE`. Digitar é estado efêmero de
   * presença, então fica na mesma vizinhança em vez de junto dos opcodes de voz.
   *
   * Não existe `TYPING_STOP`. O servidor não guarda estado disto — ele
   * reencaminha e esquece, e o indicador do cliente expira sozinho. Um "parei de
   * digitar" seria um evento a mais para carregar a informação que a ausência
   * do primeiro já carrega, e ele se perde numa reconexão, deixando o indicador
   * preso na tela de quem o perdeu.
   */
  TYPING: 4,
  /** C→S · retoma sessão sem perder eventos */
  RESUME: 6,
  /** S→C · servidor pede reconexão (deploy, rebalanceamento) */
  RECONNECT: 7,
  /** S→C · resume impossível; faça IDENTIFY completo */
  INVALID_SESSION: 9,
  /** S→C · primeiro frame; traz o intervalo de heartbeat */
  HELLO: 10,
  /** S→C · confirma o heartbeat */
  HEARTBEAT_ACK: 11,

  /** C→S · entra no canal de voz */
  VOICE_JOIN: 20,
  /** C→S · sai */
  VOICE_LEAVE: 21,
  /** C→S · mute, deaf, telas ativas */
  VOICE_STATE: 22,
  /** C↔S · encaminha SDP/ICE para UM par específico */
  SIGNAL_RELAY: 23,
} as const;

export type Opcode = (typeof Op)[keyof typeof Op];

/**
 * Códigos de fechamento. Acima de 4000 é a faixa privada do WebSocket.
 *
 * `recoverable` diz ao cliente se vale tentar RESUME. Sem essa distinção, um
 * cliente com token inválido reconectaria em laço infinito.
 */
export const CloseCode = {
  UNKNOWN_ERROR: { code: 4000, recoverable: true },
  UNKNOWN_OPCODE: { code: 4001, recoverable: true },
  DECODE_ERROR: { code: 4002, recoverable: true },
  NOT_AUTHENTICATED: { code: 4003, recoverable: false },
  AUTHENTICATION_FAILED: { code: 4004, recoverable: false },
  ALREADY_AUTHENTICATED: { code: 4005, recoverable: true },
  INVALID_SEQUENCE: { code: 4007, recoverable: false },
  RATE_LIMITED: { code: 4008, recoverable: true },
  SESSION_TIMEOUT: { code: 4009, recoverable: false },
  INVALID_VERSION: { code: 4010, recoverable: false },
  SESSION_REVOKED: { code: 4011, recoverable: false },
} as const;

/* ------------------------------------------------------------------------------
   Envelope
------------------------------------------------------------------------------ */

/**
 * `s` (sequência) só vem em DISPATCH; é o que o RESUME usa como marca d'água.
 * `t` é o nome do evento — string e não número, para o log ser legível.
 */
export const gatewayFrameSchema = z.object({
  op: z.number().int(),
  d: z.unknown().optional(),
  s: z.number().int().nonnegative().optional(),
  t: z.string().optional(),
});

export type GatewayFrame = z.infer<typeof gatewayFrameSchema>;

/* ------------------------------------------------------------------------------
   Handshake
------------------------------------------------------------------------------ */

export const helloSchema = z.object({
  heartbeat_interval_ms: z.number().int().positive(),
  /** Ecoado para o cliente detectar proxy que reescreve o query string. */
  gateway_version: z.number().int(),
});

export const identifySchema = z.object({
  token: z.string().min(1),
  /** Informativo, para a lista de sessões e a auditoria. */
  device: z.object({
    name: z.string().max(120),
    platform: z.enum(['windows', 'macos', 'linux', 'web', 'android', 'ios']),
    app_version: z.string().max(40),
  }),
  /** Capacidades do cliente. O servidor evita mandar o que ele não entende. */
  capabilities: z
    .object({
      /** Suporta múltiplas telas simultâneas. */
      multi_screen: z.boolean().default(false),
      /** Suporta canais E2EE (tem keystore). */
      e2ee: z.boolean().default(false),
    })
    .default({ multi_screen: false, e2ee: false }),
});

export const resumeSchema = z.object({
  token: z.string().min(1),
  session_id: z.string().min(1),
  /** Último `s` recebido. O servidor reenvia tudo acima disso. */
  seq: z.number().int().nonnegative(),
});

export const heartbeatSchema = z.object({
  seq: z.number().int().nonnegative().nullable(),
});

/* ------------------------------------------------------------------------------
   Digitando
------------------------------------------------------------------------------ */

/**
 * O cliente manda só o canal.
 *
 * Quem está digitando o SERVIDOR sabe — é o dono da conexão. Aceitar um
 * `user_id` do cliente deixaria qualquer pessoa fazer o indicador dizer que
 * outra está digitando, que é falsificação de presença barata.
 */
export const typingSchema = z.object({
  channel_id: z.string().uuid(),
});

/* ------------------------------------------------------------------------------
   Voz e sinalização
------------------------------------------------------------------------------ */

export const voiceJoinSchema = z.object({
  channel_id: z.string().uuid(),
  self_mute: z.boolean().default(false),
  self_deaf: z.boolean().default(false),
});

export const voiceStateSchema = z.object({
  self_mute: z.boolean().optional(),
  self_deaf: z.boolean().optional(),
  camera_on: z.boolean().optional(),
  /**
   * Quantas telas este par está compartilhando.
   *
   * Não é cosmético: cada par usa esse número no próprio governador de banda,
   * porque o custo de upload de todos depende de quantas telas existem na sala.
   * Ver docs/ARCHITECTURE.md §9.
   */
  screen_count: z.number().int().min(0).max(8).optional(),
});

/**
 * Sinalização é relay CEGO. O servidor valida três coisas e nada mais:
 * remetente está no canal, destinatário está no MESMO canal, payload cabe no
 * limite. Ele NÃO interpreta SDP — não reescreve, não filtra codec, não
 * inspeciona.
 *
 * É o que mantém o servidor fora do plano de mídia por construção, e não por
 * disciplina de quem escreve o código.
 */
export const signalRelaySchema = z.object({
  /** Par de destino. */
  to_user_id: z.string().uuid(),
  /** Sessão de destino: o mesmo usuário pode estar em dois dispositivos. */
  to_session_id: z.string().min(1),
  payload: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('offer'), sdp: z.string().max(64_000) }),
    z.object({ kind: z.literal('answer'), sdp: z.string().max(64_000) }),
    z.object({
      kind: z.literal('ice'),
      candidate: z.string().max(2_000),
      sdp_mid: z.string().max(120).nullable(),
      sdp_m_line_index: z.number().int().nonnegative().nullable(),
      username_fragment: z.string().max(256).nullable().optional(),
    }),
    /** Fim dos candidatos, para o par não esperar por mais. */
    z.object({ kind: z.literal('ice_complete') }),
    /** Pedido de ICE restart após queda de rota. */
    z.object({ kind: z.literal('restart') }),
  ]),
});

/**
 * Credenciais ICE devolvidas no VOICE_JOIN.
 *
 * TTL curto de propósito: a credencial TURN é HMAC do segredo compartilhado,
 * expira em minutos e é rastreável ao usuário. Não existe senha estática de
 * TURN no sistema.
 */
export const iceServersSchema = z.object({
  ice_servers: z.array(
    z.object({
      urls: z.array(z.string()),
      username: z.string().optional(),
      credential: z.string().optional(),
    }),
  ),
  /** Instante de expiração, para o cliente renovar antes de precisar. */
  expires_at: z.string().datetime(),
});

/* ------------------------------------------------------------------------------
   Eventos de domínio (DISPATCH)
------------------------------------------------------------------------------ */

export const GatewayEvent = {
  READY: 'READY',
  RESUMED: 'RESUMED',

  MESSAGE_CREATE: 'MESSAGE_CREATE',
  MESSAGE_UPDATE: 'MESSAGE_UPDATE',
  MESSAGE_DELETE: 'MESSAGE_DELETE',
  REACTION_ADD: 'REACTION_ADD',
  REACTION_REMOVE: 'REACTION_REMOVE',
  TYPING_START: 'TYPING_START',

  CHANNEL_CREATE: 'CHANNEL_CREATE',
  CHANNEL_UPDATE: 'CHANNEL_UPDATE',
  CHANNEL_DELETE: 'CHANNEL_DELETE',

  GUILD_UPDATE: 'GUILD_UPDATE',
  GUILD_MEMBER_ADD: 'GUILD_MEMBER_ADD',
  GUILD_MEMBER_UPDATE: 'GUILD_MEMBER_UPDATE',
  GUILD_MEMBER_REMOVE: 'GUILD_MEMBER_REMOVE',
  GUILD_ROLE_CREATE: 'GUILD_ROLE_CREATE',
  GUILD_ROLE_UPDATE: 'GUILD_ROLE_UPDATE',
  GUILD_ROLE_DELETE: 'GUILD_ROLE_DELETE',

  PRESENCE_UPDATE: 'PRESENCE_UPDATE',

  /** Confirma a entrada e traz o roster + credenciais ICE. */
  VOICE_READY: 'VOICE_READY',
  VOICE_PEER_JOIN: 'VOICE_PEER_JOIN',
  VOICE_PEER_LEAVE: 'VOICE_PEER_LEAVE',
  VOICE_STATE_UPDATE: 'VOICE_STATE_UPDATE',
  /** Sinalização encaminhada por outro par. */
  SIGNAL: 'SIGNAL',
} as const;

export type GatewayEventName = (typeof GatewayEvent)[keyof typeof GatewayEvent];

/**
 * Um par no roster de voz.
 *
 * `session_id` é o que decide o papel polite/impolite no perfect negotiation:
 * os dois lados comparam as strings e chegam ao mesmo resultado sem
 * round-trip. Ver docs/ARCHITECTURE.md §8.
 */
export const voicePeerSchema = z.object({
  user_id: z.string().uuid(),
  session_id: z.string().min(1),
  /*
   * O nome viaja COM o roster, e nao e resolvido depois.
   *
   * O DEFEITO que isto corrige
   * ==========================
   * O roster carregava so `user_id` e `session_id`. O cliente entao procurava o
   * nome na lista de membros vinda do REST -- e essa lista e carregada UMA vez,
   * ao abrir o servidor. Consequencias, todas observadas em uso:
   *
   *   · quem entra na voz depois disso nao esta na lista e aparece no card como
   *     "Participante";
   *   · a lista de quem esta na sala, na lateral, so muda ao RECARREGAR a
   *     pagina, porque ela e derivada da lista de membros e nao dos eventos;
   *   · o nome nao aparece no card do participante.
   *
   * Um roster que nao se descreve obriga quem o recebe a manter outra fonte de
   * verdade sincronizada. Aqui a outra fonte nao estava sincronizada -- e nem
   * poderia estar, porque nada a invalidava.
   */
  username: z.string(),
  display_name: z.string().nullable(),
  self_mute: z.boolean(),
  self_deaf: z.boolean(),
  camera_on: z.boolean(),
  screen_count: z.number().int().min(0),
});

export const voiceReadySchema = z.object({
  channel_id: z.string().uuid(),
  session_id: z.string().min(1),
  /** Pares JÁ na sala. O cliente abre uma RTCPeerConnection para cada. */
  peers: z.array(voicePeerSchema),
  ice: iceServersSchema,
  /** Teto de participantes, para a UI avisar antes de encher. */
  max_participants: z.number().int().positive(),
});

/* ------------------------------------------------------------------------------
   Limites
   ------------------------------------------------------------------------------
   Ficam no contrato, e não só na configuração do servidor, para o cliente poder
   validar antes de enviar e dar erro local em vez de ser desconectado.
------------------------------------------------------------------------------ */
export const GatewayLimits = {
  /** Quadro maior que isto é rejeitado. SDP de mesh com várias telas é grande. */
  MAX_FRAME_BYTES: 128 * 1024,
  /** Quadros por segundo por conexão, fora sinalização. */
  MAX_FRAMES_PER_SECOND: 30,
  /** Sinalização tem limite próprio, maior: ICE trickle é rajada. */
  MAX_SIGNAL_FRAMES_PER_SECOND: 60,
  /** Tolerância de heartbeat perdido antes de considerar a sessão morta. */
  HEARTBEAT_MISS_TOLERANCE: 2,
  /** Janela de RESUME. Passado isto, INVALID_SESSION. */
  RESUME_WINDOW_MS: 5 * 60 * 1000,
  /** Teto do buffer de RESUME por sessão. */
  RESUME_BUFFER_EVENTS: 1_000,
} as const;

/* ------------------------------------------------------------------------------
   Helpers de tipo
------------------------------------------------------------------------------ */

export type Hello = z.infer<typeof helloSchema>;
export type Identify = z.infer<typeof identifySchema>;
export type Resume = z.infer<typeof resumeSchema>;
export type Heartbeat = z.infer<typeof heartbeatSchema>;
export type VoiceJoin = z.infer<typeof voiceJoinSchema>;
export type VoiceStateUpdate = z.infer<typeof voiceStateSchema>;
export type SignalRelay = z.infer<typeof signalRelaySchema>;
export type IceServers = z.infer<typeof iceServersSchema>;
export type VoicePeer = z.infer<typeof voicePeerSchema>;
export type VoiceReady = z.infer<typeof voiceReadySchema>;

/**
 * Decide o papel no perfect negotiation.
 *
 * Comparação lexicográfica dos session_id: os dois lados computam localmente e
 * chegam ao mesmo resultado, sem coordenação do servidor. O "polite" cede em
 * caso de colisão de offers (glare).
 *
 * Fica aqui, no pacote compartilhado, para servidor e cliente nunca divergirem
 * na regra — divergência produziria os dois lados polite (ninguém oferece) ou
 * os dois impolite (deadlock de negociação).
 */
export function isPolitePeer(mySessionId: string, peerSessionId: string): boolean {
  if (mySessionId === peerSessionId) {
    throw new Error(
      'session_id idêntico dos dois lados: impossível decidir polite/impolite. ' +
        'Indica bug de geração de sessão no servidor.',
    );
  }
  return mySessionId < peerSessionId;
}
